import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign,
  MapPin,
  ArrowUp,
  ArrowDown,
  Home,
  Building,
  Users as UsersIcon,
  ArrowLeft,
  Globe,
  Eye,
  User,
  BarChart3,
  Phone,
  FileText,
  ShoppingCart,
  TrendingUp,
  Inbox,
  Clock,
  CalendarPlus,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Legend, Cell, LabelList } from 'recharts';
import apiService from './services/api';
import { Panel, Section, LoadingSpinner } from './components';

function MetricsPage() {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointments, setAppointments] = useState([]);

  const [revenueTargets, setRevenueTargets] = useState([]);

  // Website traffic state
  const [websiteTraffic, setWebsiteTraffic] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(true);

  // Traffic filter state
  const [trafficDateFilter, setTrafficDateFilter] = useState('all');
  const [trafficCustomFrom, setTrafficCustomFrom] = useState('');
  const [trafficCustomTo, setTrafficCustomTo] = useState('');
  const [trafficSourceFilter, setTrafficSourceFilter] = useState('all');

  // Requests metrics state
  const [requestsData, setRequestsData] = useState(null);
  const [requestsLoading, setRequestsLoading] = useState(true);

  // Bookings metrics state
  const [bookingsData, setBookingsData] = useState(null);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsDays, setBookingsDays] = useState(30);
  const [bookingsBookedBy, setBookingsBookedBy] = useState('');
  const [bookingsBookerList, setBookingsBookerList] = useState([]);

  // Client LTV state
  const [ltvData, setLtvData] = useState(null);
  const [ltvLoading, setLtvLoading] = useState(false);

  // Retention state
  const [retentionData, setRetentionData] = useState(null);
  const [retentionLoading, setRetentionLoading] = useState(false);

  // View state
  const [activeTab, setActiveTab] = useState('overview');

  // Leads metrics state
  const [leadsData, setLeadsData] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsDays, setLeadsDays] = useState(30);
  const [leadsGroupBy, setLeadsGroupBy] = useState('day');
  const [leadsHideNotLead, setLeadsHideNotLead] = useState(true);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'detail'
  const [selectedYear, setSelectedYear] = useState(2026);
  const [periodType, setPeriodType] = useState('month'); // 'month' | 'quarter'
  const [expandBooked, setExpandBooked] = useState(false);
  const [expandCancelled, setExpandCancelled] = useState(false);

  // Auto-set viewMode based on selectedLocation
  useEffect(() => {
    setViewMode(selectedLocation ? 'detail' : 'overview');
  }, [selectedLocation]);

  // Fetch data
  useEffect(() => {
    fetchData();
    fetchTargets();
    fetchWebsiteTraffic();
    fetchRequestsMetrics();
    fetchBookingsMetrics();
    apiService.get('/amn/metrics/leads-monthly/', { months: 6 }).then(data => setMonthlyData(data?.months || [])).catch(() => {});
    apiService.get('/amn/metrics/leads-by-person/', { days: 30, page_size: 5 }).then(data => setLeadsData(data)).catch(() => {});
  }, []);

  // Re-fetch traffic when date filter changes
  useEffect(() => {
    if (trafficDateFilter === 'custom') {
      if (trafficCustomFrom && trafficCustomTo) fetchWebsiteTraffic();
    } else {
      fetchWebsiteTraffic();
    }
  }, [trafficDateFilter, trafficCustomFrom, trafficCustomTo]);

  // Reset source filter if selected source no longer exists in data
  useEffect(() => {
    if (websiteTraffic && trafficSourceFilter !== 'all') {
      const sources = (websiteTraffic.traffic_sources || []).map(s => s.source);
      if (!sources.includes(trafficSourceFilter)) {
        setTrafficSourceFilter('all');
      }
    }
  }, [websiteTraffic]);

  const fetchWebsiteTraffic = async () => {
    try {
      setTrafficLoading(true);
      const params = {};
      const today = new Date();
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

      switch (trafficDateFilter) {
        case 'today':
          params.dateFrom = fmt(today);
          params.dateTo = fmt(today);
          break;
        case 'this_week': {
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          params.dateFrom = fmt(startOfWeek);
          params.dateTo = fmt(today);
          break;
        }
        case 'this_month': {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          params.dateFrom = fmt(startOfMonth);
          params.dateTo = fmt(today);
          break;
        }
        case 'last_30':
          params.days = 30;
          break;
        case 'last_7':
          params.days = 7;
          break;
        case 'custom':
          if (trafficCustomFrom && trafficCustomTo) {
            params.dateFrom = trafficCustomFrom;
            params.dateTo = trafficCustomTo;
          }
          break;
        case 'all':
        default:
          break;
      }
      const data = await apiService.getWebsiteTraffic(params);
      setWebsiteTraffic(data);
    } catch (err) {
      console.error('Error fetching website traffic:', err);
    } finally {
      setTrafficLoading(false);
    }
  };

  const fetchRequestsMetrics = async () => {
    try {
      setRequestsLoading(true);
      const data = await apiService.getRequestsMetrics(30);
      setRequestsData(data);
    } catch (err) {
      console.error('Error fetching requests metrics:', err);
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchBookingsMetrics = async () => {
    try {
      setBookingsLoading(true);
      const data = await apiService.getBookingsMetrics(bookingsDays, bookingsBookedBy || null);
      setBookingsData(data);
      if (!bookingsBookedBy && data?.by_booker) {
        setBookingsBookerList(data.by_booker);
      }
    } catch (err) {
      console.error('Error fetching bookings metrics:', err);
    } finally {
      setBookingsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingsMetrics();
  }, [bookingsDays, bookingsBookedBy]);

  // Re-fetch targets when year changes
  useEffect(() => {
    fetchTargets();
  }, [selectedYear]);

  const fetchData = async () => {
    // Initial load handled by fetchYearData
  };

  // Fetch aggregated data for the selected year
  useEffect(() => {
    fetchYearData();
  }, [selectedYear]);

  const fetchYearData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getRevenueByLocation({ year: selectedYear });
      // Convert aggregated rows into appointment-like objects for existing useMemo logic
      const syntheticAppointments = (data.data || []).map((row, i) => ({
        id: `agg-${i}`,
        date_time: `${selectedYear}-${String(row.month).padStart(2, '0')}-15T12:00:00Z`,
        total_payment: String(row.revenue),
        is_alf: row.type === 'alf',
        is_event: row.type === 'event',
        location_id: row.location_id,
        location_name: row.location_name,
        _count: row.count,
      }));
      setAppointments(syntheticAppointments);
    } catch (err) {
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async () => {
    try {
      const data = await apiService.getTargets({ metric: 'revenue', year: selectedYear });
      setRevenueTargets(data.results || data || []);
    } catch (err) {
      console.error('Error fetching targets:', err);
    }
  };

  // Build target lookup: month number -> target value
  const targetByMonth = useMemo(() => {
    const map = {};
    revenueTargets.forEach(t => {
      if (t.year === selectedYear) {
        map[t.month] = parseFloat(t.target_value);
      }
    });
    return map;
  }, [revenueTargets, selectedYear]);

  // Monthly actual vs target chart data
  const monthlyComparison = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Sum actual revenue by month from appointments
    const actualByMonth = {};
    appointments.forEach(apt => {
      if (!apt.total_payment) return;
      const date = new Date(apt.date_time);
      if (date.getFullYear() !== selectedYear) return;
      const m = date.getMonth() + 1;
      actualByMonth[m] = (actualByMonth[m] || 0) + parseFloat(apt.total_payment);
    });

    return monthNames.map((name, i) => {
      const month = i + 1;
      return {
        month: name,
        actual: Math.round(actualByMonth[month] || 0),
        target: Math.round(targetByMonth[month] || 0),
      };
    });
  }, [appointments, targetByMonth, selectedYear]);

  // Click handlers
  const handleLocationClick = (locationId) => {
    setSelectedLocation(locationId);
  };

  const handleBackClick = () => {
    setSelectedLocation(null);
  };

  // Utility functions
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getAppointmentType = (appointment) => {
    if (appointment.is_alf) return 'alf';
    if (appointment.is_event) return 'event';
    return 'home';
  };

  const getLocationName = (appointment) => {
    if (appointment.location_name) return appointment.location_name;
    return 'Unknown Location';
  };

  const getLocationId = (appointment) => {
    if (appointment.location_id) return appointment.location_id;
    return 0;
  };

  const getPeriodKey = (dateString, type) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const quarter = Math.ceil(month / 3);

    if (type === 'month') {
      return `${year}-${String(month).padStart(2, '0')}`;
    } else {
      return `${year}-Q${quarter}`;
    }
  };

  const getPeriodLabel = (periodKey, type) => {
    if (type === 'month') {
      const [year, month] = periodKey.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else {
      return periodKey;
    }
  };

  const calculateGrowth = (periods, byPeriod) => {
    if (periods.length < 2) return null;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentQuarter = Math.ceil(currentMonth / 3);

    const latest = periods[periods.length - 1];

    // Check if the latest period is still in progress
    let isCurrentPeriod = false;
    if (periodType === 'month') {
      const [y, m] = latest.split('-').map(Number);
      isCurrentPeriod = y === currentYear && m === currentMonth;
    } else {
      const [y, q] = latest.replace('Q', '-').split('-').map(Number);
      isCurrentPeriod = y === currentYear && q === currentQuarter;
    }

    // Compare the two most recent *complete* periods, skipping the in-progress one
    if (isCurrentPeriod) {
      if (periods.length < 3) return null;
      const prev = periods[periods.length - 2];
      const prevPrev = periods[periods.length - 3];
      const prevTotal = byPeriod[prev].total;
      const prevPrevTotal = byPeriod[prevPrev].total;
      if (prevPrevTotal === 0) return 0;
      return ((prevTotal - prevPrevTotal) / prevPrevTotal * 100).toFixed(1);
    }

    const previous = periods[periods.length - 2];
    const currentTotal = byPeriod[latest].total;
    const previousTotal = byPeriod[previous].total;

    if (previousTotal === 0) return 0;
    return ((currentTotal - previousTotal) / previousTotal * 100).toFixed(1);
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!appointments.length) {
      return {
        byLocation: [],
        byLocationPeriod: {},
        summary: { total: 0, home: 0, alf: 0, event: 0 }
      };
    }

    // Filter by selected year, exclude future months
    const now = new Date();
    const currentYearMonth = now.getFullYear() * 100 + (now.getMonth() + 1);
    const yearAppointments = appointments.filter(apt => {
      const d = new Date(apt.date_time);
      const year = d.getFullYear();
      if (year !== selectedYear) return false;
      const ym = year * 100 + (d.getMonth() + 1);
      return ym <= currentYearMonth;
    });

    // Group by location + period + type
    const grouped = {};

    yearAppointments.forEach(apt => {
      if (!apt.total_payment) return;

      const locId = getLocationId(apt);
      const locName = getLocationName(apt);
      const type = getAppointmentType(apt);
      const period = getPeriodKey(apt.date_time, periodType);
      const revenue = parseFloat(apt.total_payment);

      if (!grouped[locId]) {
        grouped[locId] = {
          locationId: locId,
          locationName: locName,
          byPeriod: {},
          total: 0,
          home: 0,
          alf: 0,
          event: 0
        };
      }

      if (!grouped[locId].byPeriod[period]) {
        grouped[locId].byPeriod[period] = { home: 0, alf: 0, event: 0, total: 0 };
      }

      grouped[locId].byPeriod[period][type] += revenue;
      grouped[locId].byPeriod[period].total += revenue;
      grouped[locId][type] += revenue;
      grouped[locId].total += revenue;
    });

    // Calculate growth for each location
    const byLocation = Object.values(grouped).map(loc => {
      const periods = Object.keys(loc.byPeriod).sort();
      return {
        ...loc,
        growth: calculateGrowth(periods, loc.byPeriod)
      };
    }).sort((a, b) => b.total - a.total);

    // Build period arrays for detail view
    const byLocationPeriod = {};
    Object.values(grouped).forEach(loc => {
      const periods = Object.keys(loc.byPeriod).sort();
      byLocationPeriod[loc.locationId] = periods.map(period => ({
        period: getPeriodLabel(period, periodType),
        periodKey: period,
        home: loc.byPeriod[period].home,
        alf: loc.byPeriod[period].alf,
        event: loc.byPeriod[period].event,
        total: loc.byPeriod[period].total
      }));
    });

    // Summary
    const summary = {
      total: byLocation.reduce((sum, loc) => sum + loc.total, 0),
      home: byLocation.reduce((sum, loc) => sum + loc.home, 0),
      alf: byLocation.reduce((sum, loc) => sum + loc.alf, 0),
      event: byLocation.reduce((sum, loc) => sum + loc.event, 0)
    };

    return { byLocation, byLocationPeriod, summary };
  }, [appointments, selectedYear, periodType]);

  // Get selected location data for detail view
  const selectedLocationData = selectedLocation
    ? metrics.byLocation.find(loc => loc.locationId === selectedLocation)
    : null;

  const selectedLocationPeriods = useMemo(() => {
    if (!selectedLocation) return [];
    const periods = metrics.byLocationPeriod[selectedLocation] || [];
    if (periodType !== 'month' || Object.keys(targetByMonth).length === 0) return periods;
    return periods.map(p => {
      const month = parseInt(p.periodKey.split('-')[1]);
      const target = targetByMonth[month];
      return target !== undefined ? { ...p, target } : p;
    });
  }, [selectedLocation, metrics.byLocationPeriod, periodType, targetByMonth]);

  // Loading state
  if (loading) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto">
        <div className="h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto">
        <Panel>
          <div className="text-center py-8">
            <h3 className="text-red-800 font-semibold">Error Loading Metrics</h3>
            <p className="text-red-600 mt-2">{error}</p>
            <button
              onClick={fetchData}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="mb-3">
        <h1 className="text-lg font-semibold text-gray-800">METRICS</h1>
        <p className="text-xs text-gray-500 mt-0.5">Business analytics and website performance</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-1 mb-3 bg-white rounded-lg p-1 shadow-sm">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'overview'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Overview
        </button>
        <button
          onClick={() => { setActiveTab('bookings'); setSelectedLocation(null); }}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'bookings'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <CalendarPlus className="w-4 h-4" />
          Bookings
        </button>
        <button
          onClick={() => setActiveTab('revenue')}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'revenue'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <DollarSign className="w-4 h-4" />
          Revenue
        </button>
        <button
          onClick={() => { setActiveTab('traffic'); setSelectedLocation(null); }}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'traffic'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Globe className="w-4 h-4" />
          Website Traffic
        </button>
        <button
          onClick={() => {
            setActiveTab('clients');
            if (!ltvData && !ltvLoading) {
              setLtvLoading(true);
              apiService.get('/amn/metrics/client-ltv/').then(data => {
                setLtvData(data);
                setLtvLoading(false);
              }).catch(() => setLtvLoading(false));
            }
          }}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'clients'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <UsersIcon className="w-4 h-4" />
          Client LTV
        </button>
        <button
          onClick={() => {
            setActiveTab('retention');
            if (!retentionData && !retentionLoading) {
              setRetentionLoading(true);
              apiService.get('/amn/metrics/retention/').then(data => {
                setRetentionData(data);
                setRetentionLoading(false);
              }).catch(() => setRetentionLoading(false));
            }
          }}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'retention'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Retention
        </button>
        <button
          onClick={() => {
            setActiveTab('leads');
            if (!leadsLoading) {
              setLeadsLoading(true);
              Promise.all([
                apiService.get('/amn/metrics/leads-by-person/', { days: leadsDays, page_size: 500 }),
                apiService.get('/amn/metrics/leads-monthly/', { months: 6 }),
              ]).then(([leads, monthly]) => {
                setLeadsData(leads);
                setMonthlyData(monthly?.months || []);
                setLeadsLoading(false);
              }).catch(() => setLeadsLoading(false));
            }
          }}
          className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
            activeTab === 'leads'
              ? 'bg-red-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Inbox className="w-4 h-4" />
          Leads
        </button>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <>
          {/* KPI Scorecard */}
          {monthlyData?.length >= 2 && (() => {
            const complete = monthlyData.filter(m => m.month < new Date().toISOString().slice(0, 7));
            const latest = complete[complete.length - 1];
            const prev = complete[complete.length - 2];
            if (!latest) return null;
            const gradeCalc = (val, thresholds) => {
              if (val >= thresholds[0]) return { letter: 'A', cls: 'bg-green-100 text-green-800 border-green-200' };
              if (val >= thresholds[1]) return { letter: 'B', cls: 'bg-blue-100 text-blue-800 border-blue-200' };
              if (val >= thresholds[2]) return { letter: 'C', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
              return { letter: 'D', cls: 'bg-red-100 text-red-800 border-red-200' };
            };
            const deltaCalc = (curr, p) => p > 0 ? ((curr - p) / p * 100).toFixed(0) : null;
            const kpis = [
              { label: 'Revenue', value: latest.revenue, unit: '$', prev: prev?.revenue, g: gradeCalc(latest.revenue, [30000, 25000, 20000]) },
              { label: 'Appointments', value: latest.appointments, unit: '#', prev: prev?.appointments, g: gradeCalc(latest.appointments, [250, 200, 150]) },
              { label: 'Retention', value: latest.retention_rate, unit: '%', target: 70, prev: prev?.retention_rate, g: gradeCalc(latest.retention_rate, [75, 60, 45]) },
              { label: 'Lead Conv.', value: latest.conversion_rate, unit: '%', target: 40, prev: prev?.conversion_rate, g: gradeCalc(latest.conversion_rate, [40, 30, 20]) },
              { label: 'New Client Return', value: latest.new_rebook_rate_60d, unit: '%', target: 30, prev: prev?.new_rebook_rate_60d, g: gradeCalc(latest.new_rebook_rate_60d, [35, 25, 15]) },
            ];
            return (
              <Panel className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Business Scorecard — {latest.month_label}</h3>
                  <p className="text-[10px] text-gray-400">vs {prev?.month_label}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {kpis.map(k => {
                    const d = k.prev != null ? deltaCalc(k.value, k.prev) : null;
                    return (
                      <div key={k.label} className={`rounded-lg border px-3 py-2 ${k.g?.cls || 'bg-gray-50 border-gray-200'}`}>
                        <p className="text-[10px] font-medium opacity-70">{k.label}</p>
                        <div className="flex items-baseline gap-1.5">
                          <p className="text-lg font-bold">
                            {k.unit === '$' ? `$${(k.value/1000).toFixed(1)}k` : k.unit === '#' ? k.value : `${k.value}%`}
                          </p>
                          {k.g && <span className="text-xs font-bold opacity-60">{k.g.letter}</span>}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {k.target && <span className="text-[9px] opacity-50">target: {k.target}%</span>}
                          {d !== null && (
                            <span className={`text-[9px] font-medium ${Number(d) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                              {Number(d) >= 0 ? '↑' : '↓'}{Math.abs(Number(d))}%
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            );
          })()}

          {/* YTD Pipeline (from bookings data) */}
          {bookingsData?.ytd_pipeline && (
            <Panel className="mb-3 !p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">YTD Revenue Pipeline</p>
                {bookingsData.ytd_pipeline.change_today && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    bookingsData.ytd_pipeline.change_today.revenue >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {bookingsData.ytd_pipeline.change_today.revenue >= 0 ? '↑' : '↓'} ${Math.abs(Math.round(bookingsData.ytd_pipeline.change_today.revenue)).toLocaleString()} today
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-bold text-gray-900">${Math.round(bookingsData.ytd_pipeline.revenue).toLocaleString()}</span>
                <span className="text-xs text-gray-500">{bookingsData.ytd_pipeline.count} appointments</span>
              </div>
              {bookingsData.ytd_pipeline.months && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                  {bookingsData.ytd_pipeline.months.slice(-6).map(m => (
                    <div key={m.label} className={`rounded-lg px-2 py-1.5 ${m.is_current ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className={`text-[10px] font-semibold ${m.is_current ? 'text-red-700' : 'text-gray-600'}`}>{m.label}</p>
                      <p className="text-sm font-bold text-gray-900">${(m.revenue / 1000).toFixed(1)}k</p>
                      <p className="text-[9px] text-gray-500">{m.count} appts</p>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          )}

          {/* Monthly Revenue & Appointments */}
          {monthlyData?.length > 0 && (
            <Panel className="mb-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Monthly Performance</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Month</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Revenue</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Appts</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Clients</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Retention</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Lead Conv.</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">New 60d</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Ret 60d</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {monthlyData.map((m, i) => {
                      const prev = i > 0 ? monthlyData[i - 1] : null;
                      const revDelta = prev && prev.revenue > 0 ? ((m.revenue - prev.revenue) / prev.revenue * 100) : null;
                      return (
                        <tr key={m.month} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">{m.month_label}</td>
                          <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                            ${m.revenue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                            {revDelta !== null && (
                              <span className={`ml-1 text-[10px] ${revDelta >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {revDelta >= 0 ? '↑' : '↓'}{Math.abs(revDelta).toFixed(0)}%
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-600">{m.appointments}</td>
                          <td className="px-3 py-2 text-sm text-right text-gray-600">{m.unique_clients}</td>
                          <td className="px-3 py-2 text-center">
                            {m.retention_total > 0 ? (
                              <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                                m.retention_rate >= 55 ? 'bg-green-100 text-green-800' :
                                m.retention_rate >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                              }`}>{m.retention_rate}%</span>
                            ) : '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                              m.conversion_rate >= 40 ? 'bg-green-100 text-green-800' :
                              m.conversion_rate >= 25 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                            }`}>{m.conversion_rate}%</span>
                          </td>
                          {[
                            { count: m.new_rebooked_60d, total: m.new_clients, rate: m.new_rebook_rate_60d, done: m.new_60d_complete, lo: 20, hi: 35 },
                            { count: m.ret_rebooked_60d, total: m.returning_clients, rate: m.ret_rebook_rate_60d, done: m.ret_60d_complete, lo: 50, hi: 70 },
                          ].map((col, ci) => (
                            <td key={ci} className="px-2 py-2 text-center">
                              {col.total > 0 ? (
                                <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                                  !col.done ? 'bg-gray-100 text-gray-500' :
                                  col.rate >= col.hi ? 'bg-green-100 text-green-800' :
                                  col.rate >= col.lo ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                                }`}>{col.rate}%{!col.done ? ' ⏳' : ''}</span>
                              ) : '—'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {/* Conversion by Team Member */}
          {leadsData?.by_member?.length > 0 && (
            <Panel className="mb-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Team Performance (Last 30 Days)</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Member</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Leads</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Booked</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Conv %</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Avg Resp</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leadsData.by_member.map(m => (
                      <tr key={m.user_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{m.name}</td>
                        <td className="px-3 py-2 text-sm text-center text-gray-700">{m.real_leads}</td>
                        <td className="px-3 py-2 text-sm text-center text-green-700 font-medium">{m.converted}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            m.conversion_rate >= 50 ? 'bg-green-100 text-green-800' :
                            m.conversion_rate >= 30 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                          }`}>{m.conversion_rate}%</span>
                        </td>
                        <td className="px-3 py-2 text-sm text-center text-gray-600">
                          {m.avg_response_hours != null
                            ? m.avg_response_hours < 1 ? Math.round(m.avg_response_hours * 60) + 'min'
                            : m.avg_response_hours < 24 ? Math.round(m.avg_response_hours) + 'h'
                            : Math.round(m.avg_response_hours / 24) + 'd'
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-green-700 font-medium">
                          {m.revenue > 0 ? '$' + Math.round(m.revenue).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}

      {/* WEBSITE TRAFFIC TAB */}
      {activeTab === 'traffic' && (
        <>
          {/* Traffic Filters */}
          <Panel className="mb-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
                <select
                  value={trafficDateFilter}
                  onChange={(e) => setTrafficDateFilter(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="this_week">This Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_7">Last 7 Days</option>
                  <option value="last_30">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {trafficDateFilter === 'custom' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      value={trafficCustomFrom}
                      onChange={(e) => setTrafficCustomFrom(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      value={trafficCustomTo}
                      onChange={(e) => setTrafficCustomTo(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Chart Source</label>
                <select
                  value={trafficSourceFilter}
                  onChange={(e) => setTrafficSourceFilter(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="all">All Sources</option>
                  {(websiteTraffic?.traffic_sources || []).map((src) => (
                    <option key={src.source} value={src.source}>{src.source}</option>
                  ))}
                </select>
              </div>
            </div>
          </Panel>

          {websiteTraffic ? (
            <Panel className="mb-3">
              <Section title={`Website Traffic (${
                { all: 'All Time', today: 'Today', this_week: 'This Week', this_month: 'This Month', last_7: 'Last 7 Days', last_30: 'Last 30 Days', custom: trafficCustomFrom && trafficCustomTo ? `${trafficCustomFrom} to ${trafficCustomTo}` : 'Custom Range' }[trafficDateFilter] || 'All Time'
              })`} tight>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
              <div className="bg-blue-50 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Today Visitors</p>
                    <p className="text-lg font-semibold text-gray-900">{websiteTraffic.today?.visitors || 0}</p>
                  </div>
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Yesterday Visitors</p>
                    <p className="text-lg font-semibold text-gray-900">{websiteTraffic.yesterday?.visitors || 0}</p>
                  </div>
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Total Unique Visitors</p>
                    <p className="text-lg font-semibold text-gray-900">{websiteTraffic.summary?.unique_visitors || 0}</p>
                  </div>
                  <Globe className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </div>

            {/* Daily Traffic Chart */}
            {(() => {
              const chartData = trafficSourceFilter === 'all'
                ? websiteTraffic.daily_traffic
                : (websiteTraffic.daily_traffic_by_source?.[trafficSourceFilter] || []);
              return chartData && chartData.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Daily Traffic{trafficSourceFilter !== 'all' ? ` — ${trafficSourceFilter}` : ''}
                </p>
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#9ca3af"
                      fontSize={9}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={30} />
                    <Tooltip
                      formatter={(value) => [value, 'Visitors']}
                      labelFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                    />
                    <Area type="monotone" dataKey="visitors" stroke="#3b82f6" fill="#93c5fd" name="visitors" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            );
            })()}

            {/* Top Pages */}
            {websiteTraffic.top_pages && websiteTraffic.top_pages.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Top Pages</p>
                <div className="space-y-1">
                  {websiteTraffic.top_pages.slice(0, 5).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                      <span className="text-gray-700 truncate max-w-[200px]" title={page.path}>
                        {page.path === '/' ? 'Homepage' : page.path}
                      </span>
                      <span className="font-medium text-gray-900">{page.visitors}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Traffic Sources */}
            {websiteTraffic.traffic_sources && websiteTraffic.traffic_sources.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Traffic Sources</p>
                <div className="space-y-1">
                  {websiteTraffic.traffic_sources.map((source, idx) => {
                    const total = websiteTraffic.traffic_sources.reduce((sum, s) => sum + s.visitors, 0) || 1;
                    const pct = Math.round((source.visitors / total) * 100);
                    return (
                      <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            source.source === 'Google' ? 'bg-blue-500' :
                            source.source === 'Direct' ? 'bg-green-500' :
                            source.source === 'Facebook' ? 'bg-indigo-500' :
                            source.source === 'Instagram' ? 'bg-pink-500' :
                            source.source === 'Yelp' ? 'bg-red-500' :
                            'bg-gray-400'
                          }`} />
                          <span className="text-gray-700">{source.source}</span>
                        </div>
                        <span className="font-medium text-gray-900">{source.visitors} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Conversion Metrics */}
            {websiteTraffic.conversions && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-2">Conversions</p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-amber-50 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Phone Clicks</p>
                        <p className="text-lg font-semibold text-gray-900">{websiteTraffic.conversions.phone_clicks || 0}</p>
                      </div>
                      <Phone className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Form Submissions</p>
                        <p className="text-lg font-semibold text-gray-900">{websiteTraffic.conversions.form_submissions?.total || 0}</p>
                      </div>
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Checkouts Started</p>
                        <p className="text-lg font-semibold text-gray-900">{websiteTraffic.conversions.checkouts_started?.total || 0}</p>
                      </div>
                      <ShoppingCart className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </div>

                {/* Form breakdown */}
                {(websiteTraffic.conversions.form_submissions?.total > 0 || websiteTraffic.conversions.checkouts_started?.total > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {websiteTraffic.conversions.form_submissions?.total > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Form Breakdown</p>
                        <div className="space-y-1">
                          {websiteTraffic.conversions.form_submissions.contact_forms > 0 && (
                            <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                              <span className="text-gray-700">Contact Forms</span>
                              <span className="font-medium text-gray-900">{websiteTraffic.conversions.form_submissions.contact_forms}</span>
                            </div>
                          )}
                          {websiteTraffic.conversions.form_submissions.lead_forms > 0 && (
                            <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                              <span className="text-gray-700">Lead Forms</span>
                              <span className="font-medium text-gray-900">{websiteTraffic.conversions.form_submissions.lead_forms}</span>
                            </div>
                          )}
                          {websiteTraffic.conversions.form_submissions.event_quotes > 0 && (
                            <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                              <span className="text-gray-700">Event Quotes</span>
                              <span className="font-medium text-gray-900">{websiteTraffic.conversions.form_submissions.event_quotes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {websiteTraffic.conversions.checkouts_started?.total > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Checkout Breakdown</p>
                        <div className="space-y-1">
                          {websiteTraffic.conversions.checkouts_started.bookings > 0 && (
                            <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                              <span className="text-gray-700">Bookings</span>
                              <span className="font-medium text-gray-900">{websiteTraffic.conversions.checkouts_started.bookings}</span>
                            </div>
                          )}
                          {websiteTraffic.conversions.checkouts_started.gift_cards > 0 && (
                            <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                              <span className="text-gray-700">Gift Cards</span>
                              <span className="font-medium text-gray-900">{websiteTraffic.conversions.checkouts_started.gift_cards}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Funnel rates */}
                {(websiteTraffic.conversions.funnel?.lead_rate > 0 || websiteTraffic.conversions.funnel?.checkout_rate > 0) && (
                  <div className="mt-2 flex items-center gap-4">
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                    {websiteTraffic.conversions.funnel.lead_rate > 0 && (
                      <span className="text-xs text-gray-600">
                        Lead Rate: <span className="font-semibold text-green-700">{websiteTraffic.conversions.funnel.lead_rate.toFixed(1)}%</span>
                      </span>
                    )}
                    {websiteTraffic.conversions.funnel.checkout_rate > 0 && (
                      <span className="text-xs text-gray-600">
                        Checkout Rate: <span className="font-semibold text-purple-700">{websiteTraffic.conversions.funnel.checkout_rate.toFixed(1)}%</span>
                      </span>
                    )}
                  </div>
                )}

                {/* Conversions by Source */}
                {websiteTraffic.conversions.by_source?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-700 mb-2">Conversions by Source</p>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left text-xs font-medium text-gray-500 pb-1.5">Source</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Visitors</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Phone</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Forms</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Bookings</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Gift Cards</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Total</th>
                            <th className="text-right text-xs font-medium text-gray-500 pb-1.5">Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {websiteTraffic.conversions.by_source.map((row, idx) => (
                            <tr key={idx} className="border-b border-gray-50">
                              <td className="py-1.5 text-xs font-medium text-gray-900">{row.source}</td>
                              <td className="py-1.5 text-xs text-right text-gray-600">{row.visitors}</td>
                              <td className="py-1.5 text-xs text-right text-gray-600">{row.conversions.phone_clicks || '-'}</td>
                              <td className="py-1.5 text-xs text-right text-gray-600">{(row.conversions.contact_forms + row.conversions.lead_forms + row.conversions.event_quotes) || '-'}</td>
                              <td className="py-1.5 text-xs text-right text-gray-600">{row.conversions.bookings || '-'}</td>
                              <td className="py-1.5 text-xs text-right text-gray-600">{row.conversions.gift_cards || '-'}</td>
                              <td className="py-1.5 text-xs text-right font-semibold text-gray-900">{row.total_conversions}</td>
                              <td className="py-1.5 text-xs text-right">
                                <span className={`font-semibold ${row.conversion_rate >= 20 ? 'text-green-700' : row.conversion_rate >= 10 ? 'text-amber-600' : 'text-gray-600'}`}>
                                  {row.conversion_rate.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Section>
        </Panel>
          ) : (
            <Panel className="mb-3">
              <div className="text-center py-8 text-sm text-gray-500">
                {trafficLoading ? 'Loading traffic data...' : 'No traffic data available'}
              </div>
            </Panel>
          )}
        </>
      )}

      {/* REQUESTS TAB */}
      {activeTab === 'requests' && (
        <>
          {requestsLoading ? (
            <Panel className="mb-3">
              <div className="text-center py-8 text-sm text-gray-500">
                Loading requests data...
              </div>
            </Panel>
          ) : requestsData ? (
            <Panel className="mb-3">
              <Section title="Active & Snoozed Requests (Last 30 Days)" tight>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-blue-50 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Active Requests</p>
                        <p className="text-lg font-semibold text-gray-900">{requestsData.totals?.active || 0}</p>
                        <p className="text-xs text-gray-400">new, follow-up, existing client</p>
                      </div>
                      <Inbox className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Snoozed Requests</p>
                        <p className="text-lg font-semibold text-gray-900">{requestsData.totals?.snoozed || 0}</p>
                        <p className="text-xs text-gray-400">on hold for later</p>
                      </div>
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </div>

                {/* Daily Chart */}
                {requestsData.daily && requestsData.daily.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Daily Breakdown</p>
                    <ResponsiveContainer width="100%" height={180}>
                      <ComposedChart data={requestsData.daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="#9ca3af"
                          fontSize={9}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={30} />
                        <Tooltip
                          formatter={(value, name) => [value, name === 'active' ? 'Active' : 'Snoozed']}
                          labelFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                        />
                        <Bar dataKey="active" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Active" />
                        <Bar dataKey="snoozed" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Snoozed" />
                      </ComposedChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-center gap-4 mt-1 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 bg-blue-500 rounded-sm" />
                        <span>Active</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 bg-amber-500 rounded-sm" />
                        <span>Snoozed</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Period Info */}
                {requestsData.period && (
                  <div className="text-xs text-gray-400 text-center">
                    {requestsData.period.start} to {requestsData.period.end}
                  </div>
                )}
              </Section>
            </Panel>
          ) : (
            <Panel className="mb-3">
              <div className="text-center py-8 text-sm text-gray-500">
                No requests data available
              </div>
            </Panel>
          )}
        </>
      )}

      {/* BOOKINGS TAB */}
      {activeTab === 'bookings' && (
        <>
          {/* Period selector */}
          <Panel className="mb-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-700">Period</label>
                <select
                  value={bookingsDays}
                  onChange={(e) => setBookingsDays(parseInt(e.target.value))}
                  className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value={7}>Last 7 Days</option>
                  <option value={14}>Last 14 Days</option>
                  <option value={30}>Last 30 Days</option>
                  <option value={60}>Last 60 Days</option>
                  <option value={90}>Last 90 Days</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-700">Booked By</label>
                <select
                  value={bookingsBookedBy}
                  onChange={(e) => setBookingsBookedBy(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="">All Staff</option>
                  {bookingsBookerList.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </Panel>

          {bookingsLoading ? (
            <Panel className="mb-3">
              <div className="text-center py-8 text-sm text-gray-500">Loading bookings data...</div>
            </Panel>
          ) : bookingsData ? (
            <>
              {/* YTD Pipeline */}
              {bookingsData.ytd_pipeline && (
                <Panel className="mb-3 !p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">YTD Revenue Pipeline</p>
                    {bookingsData.ytd_pipeline.change_today ? (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        bookingsData.ytd_pipeline.change_today.revenue >= 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {bookingsData.ytd_pipeline.change_today.revenue >= 0 ? '↑' : '↓'} ${Math.abs(Math.round(bookingsData.ytd_pipeline.change_today.revenue)).toLocaleString()} new bookings today
                        {bookingsData.ytd_pipeline.change_today.count !== 0 && ` (${bookingsData.ytd_pipeline.change_today.count > 0 ? '+' : ''}${bookingsData.ytd_pipeline.change_today.count})`}
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-400">delta available tomorrow</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-gray-900">${Math.round(bookingsData.ytd_pipeline.revenue).toLocaleString()}</span>
                    <span className="text-xs text-gray-500">{bookingsData.ytd_pipeline.count} appointments</span>
                  </div>
                  {bookingsData.ytd_pipeline.months && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {bookingsData.ytd_pipeline.months.map(m => (
                        <div key={m.label} className={`rounded-lg px-3 py-2 ${
                          m.is_current ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <p className={`text-xs font-semibold ${m.is_current ? 'text-red-700' : 'text-blue-700'}`}>{m.label}</p>
                            {m.is_current && <span className="text-[9px] bg-red-200 text-red-800 px-1 rounded">Current</span>}
                          </div>
                          <p className="text-lg font-bold text-gray-900">${(m.revenue / 1000).toFixed(1)}k</p>
                          <p className="text-[10px] text-gray-500">{m.count} appointments</p>
                          {m.change_today && (m.change_today.revenue !== 0 || m.change_today.count !== 0) && (
                            <p className={`text-[10px] font-semibold mt-0.5 ${m.change_today.revenue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {m.change_today.revenue >= 0 ? '↑' : '↓'} ${Math.abs(Math.round(m.change_today.revenue)).toLocaleString()} new today
                              {m.change_today.count !== 0 && ` (${m.change_today.count > 0 ? '+' : ''}${m.change_today.count})`}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              )}

              {/* Today's Stats */}
              {bookingsData.today && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <Panel
                      className="!p-2 bg-green-50 border-green-200 cursor-pointer"
                      onClick={() => setExpandBooked(!expandBooked)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-green-700 font-medium">Booked Today</p>
                          <p className="text-lg font-bold text-gray-900">{bookingsData.today.booked_count || 0}</p>
                          <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.today.booked_revenue || 0).toLocaleString()}</p>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <CalendarPlus className="w-5 h-5 text-green-600" />
                          {(bookingsData.today.booked_list?.length > 0) && (
                            expandBooked
                              ? <ChevronUp className="w-3.5 h-3.5 text-green-600" />
                              : <ChevronDown className="w-3.5 h-3.5 text-green-600" />
                          )}
                        </div>
                      </div>
                    </Panel>
                    {expandBooked && bookingsData.today.booked_list?.length > 0 && (
                      <div className="mt-1 bg-white rounded-lg border border-green-200 divide-y divide-gray-100">
                        {bookingsData.today.booked_list.map((a) => (
                          <div key={a.id} className="px-2 py-1.5 flex items-center justify-between text-xs">
                            <div className="min-w-0">
                              <span className="font-medium text-gray-900 truncate block">{a.client}</span>
                              <span className="text-gray-500">{a.technician} · {a.date}</span>
                            </div>
                            <span className="font-semibold text-green-700 ml-2 whitespace-nowrap">${Math.round(a.revenue).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Panel
                      className="!p-2 bg-red-50 border-red-200 cursor-pointer"
                      onClick={() => setExpandCancelled(!expandCancelled)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-red-700 font-medium">Cancelled Today</p>
                          <p className="text-lg font-bold text-gray-900">{bookingsData.today.cancelled_count || 0}</p>
                          <p className="text-xs font-medium text-red-600">${Math.round(bookingsData.today.cancelled_revenue || 0).toLocaleString()}</p>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <XCircle className="w-5 h-5 text-red-600" />
                          {(bookingsData.today.cancelled_list?.length > 0) && (
                            expandCancelled
                              ? <ChevronUp className="w-3.5 h-3.5 text-red-600" />
                              : <ChevronDown className="w-3.5 h-3.5 text-red-600" />
                          )}
                        </div>
                      </div>
                    </Panel>
                    {expandCancelled && bookingsData.today.cancelled_list?.length > 0 && (
                      <div className="mt-1 bg-white rounded-lg border border-red-200 divide-y divide-gray-100">
                        {bookingsData.today.cancelled_list.map((a) => (
                          <div key={a.id} className="px-2 py-1.5 flex items-center justify-between text-xs">
                            <div className="min-w-0">
                              <span className="font-medium text-gray-900 truncate block">{a.client}</span>
                              <span className="text-gray-500">{a.technician} · {a.date}</span>
                            </div>
                            <span className="font-semibold text-red-700 ml-2 whitespace-nowrap">${Math.round(a.revenue).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <Panel className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">New Bookings</p>
                      <p className="text-lg font-semibold text-gray-900">{bookingsData.totals?.booked || 0}</p>
                      <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.totals?.revenue || 0).toLocaleString()}</p>
                    </div>
                    <CalendarPlus className="w-5 h-5 text-red-800" />
                  </div>
                </Panel>
                <Panel className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Weekly Avg</p>
                      <p className="text-lg font-semibold text-gray-900">{bookingsData.totals?.weekly_avg || 0}</p>
                      <p className="text-xs font-medium text-green-600">${Math.round((bookingsData.totals?.revenue || 0) / Math.max(bookingsData.period?.days / 7, 1)).toLocaleString()}/wk</p>
                    </div>
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                </Panel>
                <Panel className="p-2">
                  <div>
                    <p className="text-xs text-gray-500">Home</p>
                    <p className="text-lg font-semibold text-gray-900">{bookingsData.totals?.home || 0}</p>
                    <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.totals?.home_revenue || 0).toLocaleString()}</p>
                  </div>
                </Panel>
                <Panel className="p-2">
                  <div>
                    <p className="text-xs text-gray-500">ALF</p>
                    <p className="text-lg font-semibold text-gray-900">{bookingsData.totals?.alf || 0}</p>
                    <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.totals?.alf_revenue || 0).toLocaleString()}</p>
                  </div>
                </Panel>
                <Panel className="p-2">
                  <div>
                    <p className="text-xs text-gray-500">Event</p>
                    <p className="text-lg font-semibold text-gray-900">{bookingsData.totals?.event || 0}</p>
                    <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.totals?.event_revenue || 0).toLocaleString()}</p>
                  </div>
                </Panel>
              </div>

              {/* New vs Returning */}
              {(bookingsData.totals?.new_clients > 0 || bookingsData.totals?.returning > 0) && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Panel className="p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">New Clients</p>
                        <p className="text-lg font-semibold text-green-700">{bookingsData.totals?.new_clients || 0}</p>
                        <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.totals?.new_clients_revenue || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-400">
                          {bookingsData.totals?.booked > 0
                            ? `${Math.round((bookingsData.totals.new_clients / bookingsData.totals.booked) * 100)}% of bookings`
                            : ''}
                        </p>
                      </div>
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                  </Panel>
                  <Panel className="p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Returning Clients</p>
                        <p className="text-lg font-semibold text-blue-700">{bookingsData.totals?.returning || 0}</p>
                        <p className="text-xs font-medium text-green-600">${Math.round(bookingsData.totals?.returning_revenue || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-400">
                          {bookingsData.totals?.booked > 0
                            ? `${Math.round((bookingsData.totals.returning / bookingsData.totals.booked) * 100)}% of bookings`
                            : ''}
                        </p>
                      </div>
                      <UsersIcon className="w-5 h-5 text-blue-600" />
                    </div>
                  </Panel>
                </div>
              )}

              {/* Daily Chart */}
              {bookingsData.daily && bookingsData.daily.length > 0 && (
                <Panel className="mb-3">
                  <Section title={`Daily New Bookings (${bookingsData.period?.start} to ${bookingsData.period?.end})`} tight>
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={bookingsData.daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="#9ca3af"
                          fontSize={9}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis yAxisId="left" stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                        <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={45} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === 'revenue') return [`$${Math.round(value).toLocaleString()}`, 'Revenue'];
                            return [value, name === 'new_clients' ? 'New Clients' : 'Returning'];
                          }}
                          labelFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                        />
                        <Bar yAxisId="left" dataKey="returning" stackId="bookings" fill="#3b82f6" name="returning" />
                        <Bar yAxisId="left" dataKey="new_clients" stackId="bookings" fill="#16a34a" radius={[3, 3, 0, 0]} name="new_clients" />
                        <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} dot={false} name="revenue" />
                      </ComposedChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-center gap-4 mt-1 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 bg-green-600 rounded-sm" />
                        <span>New Clients</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 bg-blue-500 rounded-sm" />
                        <span>Returning</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-0.5 bg-amber-500" />
                        <span>Revenue</span>
                      </div>
                    </div>
                  </Section>
                </Panel>
              )}

              {/* By Booker */}
              {bookingsData.by_booker && bookingsData.by_booker.length > 0 && (
                <Panel className="mb-3">
                  <Section title="Booked By" tight>
                    <div className="space-y-1">
                      {bookingsData.by_booker.map((tech) => {
                        const pct = bookingsData.totals?.booked > 0
                          ? Math.round((tech.count / bookingsData.totals.booked) * 100)
                          : 0;
                        return (
                          <div key={tech.id} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100">
                            <div className="flex items-center gap-2 flex-1">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-gray-900 font-medium">{tech.name}</span>
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden mx-2">
                                <div className="h-full bg-red-800 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                            <span className="font-semibold text-gray-900 ml-2">{tech.count} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                          </div>
                        );
                      })}
                    </div>
                  </Section>
                </Panel>
              )}
            </>
          ) : (
            <Panel className="mb-3">
              <div className="text-center py-8 text-sm text-gray-500">No bookings data available</div>
            </Panel>
          )}
        </>
      )}

      {/* REVENUE TAB */}
      {activeTab === 'revenue' && (
        <>
          {/* Back Button (Detail Mode) */}
          {viewMode === 'detail' && (
            <button
              onClick={handleBackClick}
              className="inline-flex items-center text-sm text-gray-600 hover:text-red-800 mb-3 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Overview
            </button>
          )}

          {/* Filters */}
          <Panel className="mb-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  {[2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
                <select
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="month">By Month</option>
                  <option value="quarter">By Quarter</option>
                </select>
              </div>
            </div>
          </Panel>

          {/* OVERVIEW MODE */}
          {viewMode === 'overview' && (
        <>
          {/* Summary Cards */}
          {(() => {
            const yearlyTarget = Object.values(targetByMonth).reduce((s, v) => s + v, 0);
            const pctOfTarget = yearlyTarget > 0 ? ((metrics.summary.total / yearlyTarget) * 100).toFixed(0) : null;
            return (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                <Panel className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Total Revenue</p>
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(metrics.summary.total)}</p>
                    </div>
                    <DollarSign className="w-6 h-6 text-red-800" />
                  </div>
                </Panel>

                <Panel className="p-2">
                  <div>
                    <p className="text-xs text-gray-500">Revenue Target</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(yearlyTarget)}</p>
                    {pctOfTarget && (
                      <p className={`text-xs font-medium ${parseInt(pctOfTarget) >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
                        {pctOfTarget}% achieved
                      </p>
                    )}
                  </div>
                </Panel>

                <Panel className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Home Services</p>
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(metrics.summary.home)}</p>
                    </div>
                    <Home className="w-6 h-6 text-blue-600" />
                  </div>
                </Panel>

                <Panel className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">ALF Services</p>
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(metrics.summary.alf)}</p>
                    </div>
                    <Building className="w-6 h-6 text-purple-600" />
                  </div>
                </Panel>

                <Panel className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Event Services</p>
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(metrics.summary.event)}</p>
                    </div>
                    <UsersIcon className="w-6 h-6 text-green-600" />
                  </div>
                </Panel>
              </div>
            );
          })()}

          {/* Monthly Actual vs Target Chart */}
          {Object.keys(targetByMonth).length > 0 && (
            <Panel className="mb-3">
              <Section title={`Revenue vs Target (${selectedYear})`} tight>
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={monthlyComparison} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis
                      stroke="#9ca3af"
                      fontSize={9}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        `$${value.toLocaleString()}`,
                        name === 'actual' ? 'Actual' : 'Target'
                      ]}
                      contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="target" fill="#fbbf24" radius={[3, 3, 0, 0]} name="Target" opacity={0.4} />
                    <Bar dataKey="actual" fill="#dc2626" radius={[3, 3, 0, 0]} name="Actual" />
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-4 mt-1 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-2 bg-red-600 rounded-sm" />
                    <span>Actual</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-2 bg-amber-400 opacity-40 rounded-sm" />
                    <span>Target</span>
                  </div>
                </div>
              </Section>
            </Panel>
          )}

          {/* Location Cards */}
          {metrics.byLocation.length === 0 ? (
            <Panel>
              <div className="text-center py-8 text-sm text-gray-500">
                No revenue data available for {selectedYear}. Try selecting a different year.
              </div>
            </Panel>
          ) : (
            <>
            {/* Revenue by Location Bar Chart */}
            <Panel className="p-3 mb-3">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Revenue by Location</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={metrics.byLocation} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="locationName" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} width={100} tickFormatter={(v) => String(v || '').replace(' Area', '')} />
                  <Tooltip
                    formatter={(v, name) => [`$${v.toLocaleString()}`, { home: 'Home', alf: 'ALF', event: 'Event' }[name] || name]}
                    contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                  />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="home" stackId="rev" fill="#3b82f6" name="Home" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="alf" stackId="rev" fill="#8b5cf6" name="ALF" />
                  <Bar dataKey="event" stackId="rev" fill="#f59e0b" name="Event" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            {/* Monthly Revenue by Location */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
              {metrics.byLocation.map((loc) => {
                const periods = metrics.byLocationPeriod[loc.locationId] || [];
                if (periods.length === 0) return null;
                return (
                  <Panel key={`chart-${loc.locationId}`} className="p-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-gray-700 truncate">{String(loc.locationName || '').replace(' Area', '')}</p>
                      <p className="text-xs font-semibold text-gray-900">{formatCurrency(loc.total)}</p>
                    </div>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={periods} margin={{ left: 0, right: 0, top: 15, bottom: 0 }}>
                        <XAxis dataKey="period" stroke="#9ca3af" fontSize={8} tickLine={false} axisLine={false} />
                        <YAxis hide />
                        <Tooltip
                          formatter={(v, name) => [`$${v.toLocaleString()}`, { home: 'Home', alf: 'ALF', event: 'Event', total: 'Total' }[name] || name]}
                          contentStyle={{ fontSize: '10px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                        />
                        <Bar dataKey="home" stackId="r" fill="#3b82f6" />
                        <Bar dataKey="alf" stackId="r" fill="#8b5cf6" />
                        <Bar dataKey="event" stackId="r" fill="#f59e0b" radius={[2, 2, 0, 0]}>
                          <LabelList dataKey="total" position="top" fontSize={8} fill="#6b7280" formatter={(v) => v > 0 ? `$${(v/1000).toFixed(1)}k` : ''} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>
                );
              })}
            </div>
            </>
          )}
        </>
      )}

      {/* DETAIL MODE */}
      {viewMode === 'detail' && selectedLocationData && (
        <>
          {/* Location Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <Panel className="p-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Revenue</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedLocationData.total)}</p>
                </div>
                <DollarSign className="w-6 h-6 text-red-800" />
              </div>
            </Panel>

            <Panel className="p-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Home Services</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedLocationData.home)}</p>
                </div>
                <Home className="w-6 h-6 text-blue-600" />
              </div>
            </Panel>

            <Panel className="p-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">ALF Services</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedLocationData.alf)}</p>
                </div>
                <Building className="w-6 h-6 text-purple-600" />
              </div>
            </Panel>

            <Panel className="p-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Event Services</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedLocationData.event)}</p>
                </div>
                <UsersIcon className="w-6 h-6 text-green-600" />
              </div>
            </Panel>
          </div>

          {/* Time Series Chart */}
          {selectedLocationPeriods.length > 0 ? (
            <Panel className="mb-3">
              <Section title={`Revenue Trends (${selectedYear})`} tight>
                <ResponsiveContainer width="100%" height={140}>
                  <ComposedChart data={selectedLocationPeriods}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="period"
                      stroke="#9ca3af"
                      fontSize={9}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#9ca3af"
                      fontSize={9}
                      tickLine={false}
                      axisLine={false}
                      width={30}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value, name) => [formatCurrency(value), name === 'target' ? 'Target' : name.charAt(0).toUpperCase() + name.slice(1)]}
                      contentStyle={{ fontSize: '11px' }}
                    />
                    <Bar dataKey="home" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="alf" stackId="a" fill="#9333ea" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="event" stackId="a" fill="#16a34a" radius={[3, 3, 0, 0]} />
                    {periodType === 'month' && Object.keys(targetByMonth).length > 0 && (
                      <Line type="monotone" dataKey="target" stroke="#f59e0b" strokeWidth={2} dot={false} name="Target" />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </Section>
            </Panel>
          ) : (
            <Panel className="mb-3">
              <div className="text-center py-8 text-sm text-gray-500">
                No period data available for this location in {selectedYear}.
              </div>
            </Panel>
          )}

          {/* Breakdown Table */}
          {selectedLocationPeriods.length > 0 && (
            <Panel noPadding>
              <div className="p-2 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900">Revenue Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Home</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">ALF</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Event</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      {periodType === 'month' && Object.keys(targetByMonth).length > 0 && (
                        <>
                          <th className="px-2 py-2 text-right text-xs font-medium text-amber-600 uppercase">Target</th>
                          <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">% of Target</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedLocationPeriods.map((period, idx) => {
                      const pct = period.target ? ((period.total / period.target) * 100).toFixed(0) : null;
                      return (
                        <tr key={idx} className="hover:bg-neutral-50">
                          <td className="px-2 py-2 text-xs font-medium text-gray-900">{period.period}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(period.home)}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(period.alf)}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(period.event)}</td>
                          <td className="px-2 py-2 text-xs text-right font-semibold text-gray-900">{formatCurrency(period.total)}</td>
                          {periodType === 'month' && Object.keys(targetByMonth).length > 0 && (
                            <>
                              <td className="px-2 py-2 text-xs text-right text-amber-600">{period.target ? formatCurrency(period.target) : '-'}</td>
                              <td className={`px-2 py-2 text-xs text-right font-medium ${pct && parseInt(pct) >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
                                {pct ? `${pct}%` : '-'}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    {(() => {
                      const yearlyTarget = Object.values(targetByMonth).reduce((s, v) => s + v, 0);
                      const totalPct = yearlyTarget > 0 ? ((selectedLocationData.total / yearlyTarget) * 100).toFixed(0) : null;
                      return (
                        <tr>
                          <td className="px-2 py-2 text-xs text-gray-900">Total</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(selectedLocationData.home)}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(selectedLocationData.alf)}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(selectedLocationData.event)}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-900">{formatCurrency(selectedLocationData.total)}</td>
                          {periodType === 'month' && Object.keys(targetByMonth).length > 0 && (
                            <>
                              <td className="px-2 py-2 text-xs text-right text-amber-600">{formatCurrency(yearlyTarget)}</td>
                              <td className={`px-2 py-2 text-xs text-right font-medium ${totalPct && parseInt(totalPct) >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
                                {totalPct ? `${totalPct}%` : '-'}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}
        </>
      )}
      {/* CLIENT LTV TAB */}
      {activeTab === 'clients' && (
        <>
          {ltvLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : ltvData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Avg Client LTV</p>
                  <p className="text-2xl font-bold text-gray-900">${Math.round(ltvData.summary.avg_ltv || 0).toLocaleString()}</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Avg Revenue/Appt</p>
                  <p className="text-2xl font-bold text-green-600">${Math.round(ltvData.summary.avg_revenue_per_appt || 0).toLocaleString()}</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Repeat Rate</p>
                  <p className="text-2xl font-bold text-blue-600">{ltvData.summary.repeat_rate}%</p>
                  <p className="text-xs text-gray-400">{ltvData.summary.repeat_count} of {ltvData.summary.total_clients}</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Avg Lifespan</p>
                  <p className="text-2xl font-bold text-purple-600">{Math.round(ltvData.summary.avg_lifespan_days / 30)}mo</p>
                  <p className="text-xs text-gray-400">{ltvData.summary.avg_lifespan_days} days</p>
                </Panel>
              </div>

              {/* Revenue & Projections */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Trailing 12 Months</p>
                  <p className="text-2xl font-bold text-gray-900">${Math.round(ltvData.summary.trailing_12m_revenue || 0).toLocaleString()}</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Annual Run Rate</p>
                  <p className="text-2xl font-bold text-blue-600">${Math.round(ltvData.summary.annual_run_rate || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400">At current 3-month pace</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Growth-Adjusted Projection</p>
                  <p className="text-2xl font-bold text-emerald-600">${Math.round(ltvData.summary.growth_adjusted_rate || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400">{ltvData.summary.monthly_growth_pct > 0 ? '+' : ''}{ltvData.summary.monthly_growth_pct}% avg monthly growth (capped 5%)</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Loyal Base Projected 12m</p>
                  <p className="text-2xl font-bold text-green-600">${Math.round(ltvData.summary.total_projected_12m || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400">{ltvData.summary.active_repeat_count} active repeat clients</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Appts/Year per Active Client</p>
                  <p className="text-2xl font-bold text-indigo-600">{ltvData.summary.avg_annual_appts_per_active_client || 0}</p>
                  <p className="text-xs text-gray-400">{ltvData.summary.active_repeat_count} active clients</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Churned Clients</p>
                  <p className="text-2xl font-bold text-red-500">{ltvData.summary.churned_count}</p>
                  <p className="text-xs text-gray-400">120+ days inactive</p>
                </Panel>
              </div>

              {/* Frequency Distribution */}
              <Panel className="mb-4">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">LTV by Appointment Frequency</h3>
                </div>
                <div className="p-4">
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ltvData.distribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="min_appts" tickFormatter={v => `${v}+`} />
                        <YAxis tickFormatter={v => `$${v}`} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Avg LTV']} labelFormatter={v => `${v}+ appointments`} />
                        <Bar dataKey="avg_ltv" fill="#991b1b" radius={[4,4,0,0]}>
                          <LabelList dataKey="client_count" position="top" formatter={v => `${v}`} style={{ fontSize: 11, fill: '#666' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Panel>

              {/* Top Clients Table */}
              <Panel>
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Top Clients by Lifetime Value</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5">Client</th>
                        <th className="text-right text-xs font-medium text-gray-500 px-4 py-2.5">LTV</th>
                        <th className="text-right text-xs font-medium text-gray-500 px-4 py-2.5">Appts</th>
                        <th className="text-right text-xs font-medium text-gray-500 px-4 py-2.5">Avg/Appt</th>
                        <th className="text-right text-xs font-medium text-gray-500 px-4 py-2.5">Frequency</th>
                        <th className="text-right text-xs font-medium text-gray-500 px-4 py-2.5">Projected 12m</th>
                        <th className="text-center text-xs font-medium text-gray-500 px-4 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ltvData.clients.map(c => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{c.name}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-900 text-right font-semibold">${c.lifetime_revenue.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.appointments}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">${c.avg_per_appt}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.avg_days_between ? `Every ${c.avg_days_between}d` : '-'}</td>
                          <td className="px-4 py-2.5 text-sm text-right font-medium text-green-600">{c.projected_12m ? `$${c.projected_12m.toLocaleString()}` : '-'}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              c.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                            }`}>
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          ) : null}
        </>
      )}

      {/* RETENTION TAB */}
      {activeTab === 'retention' && (
        <>
          {retentionLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : retentionData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">1st&#8594;2nd Rebook Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{retentionData.funnel[1].pct}%</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Median Days to Rebook</p>
                  <p className="text-2xl font-bold text-blue-600">{retentionData.timing.median_days_first_to_second} days</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Avg Visit Gap</p>
                  <p className="text-2xl font-bold text-purple-600">{retentionData.timing.median_gap_between_visits} days</p>
                </Panel>
                <Panel className="p-4">
                  <p className="text-xs text-gray-500">Clients at Risk</p>
                  <p className="text-2xl font-bold text-red-600">{retentionData.at_risk.length}</p>
                </Panel>
              </div>

              {/* Retention Funnel */}
              <Panel className="mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Appointment Retention Funnel</h3>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={retentionData.funnel} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="label" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#991b1b">
                        <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 12 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              {/* Monthly Cohort Table */}
              <Panel className="mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Monthly Cohort Retention</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cohort</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">New Clients</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Returned</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Return %</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Rebooked &lt;30d</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Rebooked &lt;60d</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Rebooked &lt;90d</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Avg Rev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retentionData.cohorts.map((c, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{c.month}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.size}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.returned}</td>
                          <td className={`px-4 py-2.5 text-sm text-right font-medium ${
                            c.return_pct >= 40 ? 'text-green-600' : c.return_pct >= 25 ? 'text-yellow-600' : 'text-red-600'
                          }`}>{c.return_pct}%</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.rebooked_30d}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.rebooked_60d}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.rebooked_90d}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">${c.avg_rev}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              {/* Clients at Risk */}
              <Panel className="mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Clients at Risk (60-90 days inactive)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Client</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Phone</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Last Appt</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Days Since</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total Appts</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Lifetime Rev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {retentionData.at_risk.map((c, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{c.name}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{c.phone}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{c.last_appt_date}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.days_since}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">{c.total_appts}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600 text-right">${Math.round(c.lifetime_revenue).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          ) : null}
        </>
      )}

      {/* LEADS TAB */}
      {activeTab === 'leads' && (
        <>
          <Panel className="mb-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">Period:</label>
              <select value={leadsDays} onChange={(e) => {
                const d = parseInt(e.target.value);
                setLeadsDays(d);
                setLeadsLoading(true);
                apiService.get('/amn/metrics/leads-by-person/', { days: d, page_size: 500 }).then(data => {
                  setLeadsData(data);
                  setLeadsLoading(false);
                }).catch(() => setLeadsLoading(false));
              }} className="px-2 py-1 text-xs border border-gray-200 rounded">
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <label className="text-xs font-medium text-gray-700 ml-2">Group by:</label>
              <select value={leadsGroupBy} onChange={(e) => setLeadsGroupBy(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded">
                <option value="day">Day</option>
                <option value="week">Week</option>
              </select>
              <label className="flex items-center gap-1 ml-3 cursor-pointer">
                <input type="checkbox" checked={leadsHideNotLead} onChange={(e) => setLeadsHideNotLead(e.target.checked)}
                  className="rounded border-gray-300 text-red-800 focus:ring-red-800" />
                <span className="text-xs text-gray-600">Hide non-leads</span>
              </label>
            </div>
          </Panel>

          {leadsLoading ? (
            <Panel className="p-6"><LoadingSpinner /></Panel>
          ) : leadsData ? (() => {
            const allLeads = leadsData.leads || [];
            const leads = leadsHideNotLead ? allLeads.filter(l => l.latest_status !== 'not_a_lead') : allLeads;
            const eastern = { timeZone: 'America/New_York' };

            // Group leads by date
            const grouped = {};
            leads.forEach(l => {
              if (!l.first_contact) return;
              let key;
              if (leadsGroupBy === 'week') {
                const d = new Date(l.first_contact);
                const day = d.getDay();
                const mon = new Date(d);
                mon.setDate(mon.getDate() - ((day + 6) % 7));
                key = 'W/O ' + mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...eastern });
              } else {
                key = new Date(l.first_contact).toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short', ...eastern });
              }
              if (!grouped[key]) grouped[key] = { name: key, total: 0, booked: 0, lost: 0, follow_up: 0, not_a_lead: 0, other: 0 };
              grouped[key].total++;
              const ls = l.latest_status;
              if (ls === 'booked' || l.converted) grouped[key].booked++;
              else if (ls === 'lost') grouped[key].lost++;
              else if (ls === 'follow_up') grouped[key].follow_up++;
              else if (ls === 'not_a_lead') grouped[key].not_a_lead++;
              else grouped[key].other++;
            });

            const chartData = Object.values(grouped).reverse();
            const summary = leadsData.summary || {};

            // Source breakdown
            const sourceData = Object.entries(summary.by_source || {})
              .filter(([, v]) => v > 0)
              .map(([k, v]) => ({ name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: v }))
              .sort((a, b) => b.value - a.value);

            return (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                  <Panel className="!p-2">
                    <p className="text-xs text-gray-500">Total Leads</p>
                    <p className="text-lg font-semibold text-gray-900">{summary.total_leads || 0}</p>
                  </Panel>
                  <Panel className="!p-2">
                    <p className="text-xs text-gray-500">Converted</p>
                    <p className="text-lg font-semibold text-green-700">{summary.converted || 0}</p>
                    <p className="text-xs text-green-600">{summary.conversion_rate || 0}%</p>
                  </Panel>
                  <Panel className="!p-2">
                    <p className="text-xs text-gray-500">Avg Response</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {summary.avg_response_hours != null
                        ? summary.avg_response_hours < 1
                          ? Math.round(summary.avg_response_hours * 60) + 'min'
                          : Math.round(summary.avg_response_hours) + 'h'
                        : '—'}
                    </p>
                  </Panel>
                  <Panel className="!p-2">
                    <p className="text-xs text-gray-500">No Outreach</p>
                    <p className="text-lg font-semibold text-red-700">{summary.no_outreach_count || 0}</p>
                  </Panel>
                  <Panel className="!p-2">
                    <p className="text-xs text-gray-500">Per Day Avg</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {leadsDays > 0 ? Math.round((summary.total_leads || 0) / leadsDays * 10) / 10 : '—'}
                    </p>
                  </Panel>
                </div>

                {/* Leads Bar Chart */}
                <Panel className="mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Leads by {leadsGroupBy === 'week' ? 'Week' : 'Day'}</h3>
                  <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} barSize={leadsGroupBy === 'week' ? 40 : 20}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="booked" stackId="a" fill="#16a34a" name="Booked" />
                        <Bar dataKey="follow_up" stackId="a" fill="#eab308" name="Follow Up" />
                        <Bar dataKey="lost" stackId="a" fill="#dc2626" name="Lost" />
                        <Bar dataKey="other" stackId="a" fill="#6b7280" name="Open" />
                        <Bar dataKey="not_a_lead" stackId="a" fill="#d1d5db" name="Not Lead" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                {/* Source Breakdown */}
                <Panel className="mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Leads by Source</h3>
                  <div style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sourceData} layout="vertical" barSize={20}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                        <Bar dataKey="value" fill="#991b1b" name="Leads" radius={[0, 4, 4, 0]}>
                          <LabelList dataKey="value" position="right" style={{ fontSize: 11, fill: '#374151' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                {/* Monthly Lead Conversion */}
                {monthlyData?.length > 0 && (
                  <Panel className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Monthly Lead Conversion</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Month</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">New Leads</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Booked</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Conv %</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Lost</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Follow Up</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {monthlyData.map(m => (
                            <tr key={m.month} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm font-medium text-gray-900">{m.month_label}</td>
                              <td className="px-3 py-2 text-sm text-right text-gray-700">{m.real_leads}</td>
                              <td className="px-3 py-2 text-sm text-right text-green-700 font-medium">{m.converted}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  m.conversion_rate >= 40 ? 'bg-green-100 text-green-800' :
                                  m.conversion_rate >= 25 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                                }`}>{m.conversion_rate}%</span>
                              </td>
                              <td className="px-3 py-2 text-sm text-right text-red-600">{m.lost || '—'}</td>
                              <td className="px-3 py-2 text-sm text-right text-yellow-600">{m.follow_up || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                )}

                {/* Team Member Performance */}
                {leadsData?.by_member?.length > 0 && (
                  <Panel className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Conversion by Team Member</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Team Member</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Leads</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Booked</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Conv %</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Lost</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Follow Up</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">No Outreach</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Avg Response</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {leadsData.by_member.map(m => (
                            <tr key={m.user_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2.5 text-sm font-medium text-gray-900">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 shrink-0">
                                    {m.name?.[0] || '?'}
                                  </div>
                                  {m.name}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-sm text-center text-gray-700">{m.real_leads}</td>
                              <td className="px-3 py-2.5 text-sm text-center text-green-700 font-medium">{m.converted}</td>
                              <td className="px-3 py-2.5 text-center">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  m.conversion_rate >= 50 ? 'bg-green-100 text-green-800' :
                                  m.conversion_rate >= 30 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                                }`}>{m.conversion_rate}%</span>
                              </td>
                              <td className="px-3 py-2.5 text-sm text-center text-red-600">{m.lost || '—'}</td>
                              <td className="px-3 py-2.5 text-sm text-center text-yellow-600">{m.follow_up || '—'}</td>
                              <td className="px-3 py-2.5 text-sm text-center">
                                {m.no_outreach > 0 ? (
                                  <span className="text-red-600 font-medium">{m.no_outreach}</span>
                                ) : <span className="text-gray-400">0</span>}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-center text-gray-600">
                                {m.avg_response_hours != null
                                  ? m.avg_response_hours < 1 ? Math.round(m.avg_response_hours * 60) + 'min'
                                  : m.avg_response_hours < 24 ? Math.round(m.avg_response_hours) + 'h'
                                  : Math.round(m.avg_response_hours / 24) + 'd'
                                  : '—'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-right text-green-700 font-medium">
                                {m.revenue > 0 ? '$' + Math.round(m.revenue).toLocaleString() : '—'}
                              </td>
                            </tr>
                          ))}
                          {/* Totals row */}
                          {leadsData.by_member.length > 1 && (() => {
                            const t = leadsData.by_member.reduce((acc, m) => ({
                              real: acc.real + (m.real_leads || 0),
                              conv: acc.conv + (m.converted || 0),
                              lost: acc.lost + (m.lost || 0),
                              fu: acc.fu + (m.follow_up || 0),
                              no: acc.no + (m.no_outreach || 0),
                              rev: acc.rev + (m.revenue || 0),
                            }), { real: 0, conv: 0, lost: 0, fu: 0, no: 0, rev: 0 });
                            return (
                              <tr className="bg-gray-50 font-medium">
                                <td className="px-3 py-2 text-sm text-gray-700">Total</td>
                                <td className="px-3 py-2 text-sm text-center text-gray-700">{t.real}</td>
                                <td className="px-3 py-2 text-sm text-center text-green-700">{t.conv}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                    {t.real > 0 ? Math.round(t.conv / t.real * 100) : 0}%
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-sm text-center text-red-600">{t.lost || '—'}</td>
                                <td className="px-3 py-2 text-sm text-center text-yellow-600">{t.fu || '—'}</td>
                                <td className="px-3 py-2 text-sm text-center text-red-600">{t.no || '0'}</td>
                                <td className="px-3 py-2 text-sm text-center text-gray-600">—</td>
                                <td className="px-3 py-2 text-sm text-right text-green-700">${Math.round(t.rev).toLocaleString()}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                )}
              </>
            );
          })() : null}
        </>
      )}

      </div>
    </div>
  );
}

export default MetricsPage;
